/**
 * Opengater login flow against GlobalAuth (8005).
 *
 * Email one-time-code login — the self-contained method for an admin panel
 * (Telegram/miniapp verification is for end-users). Two steps:
 *   1. `sendCode(email)`     → POST /auth/email/send-code  (200, empty body)
 *   2. `verifyCode(email, code)` → POST /auth/email/verify → { access_token, refresh_token }
 *
 * Login itself is open to any registered user; it does NOT gate by role. The
 * section reads the current role from `/users/me` and decides which sections
 * are available — see `role.ts` / `OgAuthProvider`. A plain `user` who logs in
 * is shown an "access denied" screen rather than the admin UI.
 *
 * These two calls are unauthenticated (no token yet), so they use plain fetch
 * rather than `apiOg`. `logout` does carry the bearer.
 */
import { ApiError } from '../client';
import { ogServiceBase } from './services';
import { apiOg } from './client';
import { saveOgTokens, saveOgEmail, clearOgTokens, getOgAccessToken } from './tokens';
/** POST an unauthenticated JSON request to the auth service, throwing ApiError. */
async function authPost(path, body) {
    const base = ogServiceBase('auth');
    if (!base)
        throw new ApiError(0, null, 'Auth service is not configured (VITE_OG_AUTH_URL)');
    const res = await fetch(base + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    // Parse JSON only when there's a body — send-code replies 200 with an empty
    // body, and `res.json()` throws on empty. (Mirrors apiOg's handling.)
    const raw = await res.text();
    const ct = res.headers.get('content-type') || '';
    let data = raw;
    if (raw && ct.includes('application/json')) {
        try {
            data = JSON.parse(raw);
        }
        catch {
            data = raw;
        }
    }
    if (!res.ok)
        throw new ApiError(res.status, data, `${res.status} ${res.statusText}`);
    return data;
}
/** Step 1 — request a one-time code be emailed to `email`. */
export async function sendCode(email, language = 'ru') {
    await authPost('/auth/email/send-code', {
        email,
        service_name: 'AdminPanel',
        language,
    });
}
/**
 * Step 2 — exchange the emailed code for a JWT pair. On success the tokens and
 * the email are persisted to the OG store. The caller then reads the role from
 * GlobalAuth instead of trusting token claims.
 */
export async function verifyCode(email, code) {
    const tokens = await authPost('/auth/email/verify', {
        email,
        code,
    });
    saveOgTokens(tokens.access_token, tokens.refresh_token);
    saveOgEmail(email);
    return tokens.access_token;
}
/**
 * Log out of the Opengater section. Best-effort server-side revoke, then always
 * clear local tokens — a failed network call must still end the local session.
 */
export async function logoutOg() {
    try {
        if (getOgAccessToken()) {
            await apiOg('auth', '/auth/jwt/logout', { method: 'POST' });
        }
    }
    catch {
        /* revoke is best-effort; local clear below is what matters */
    }
    finally {
        clearOgTokens();
    }
}
